import {
  getTemplateTree,
  getTemplateLinks,
  fetchTemplateBytes,
} from "./templatesApi";
import { parseSlides, toBase64 } from "./pptx";
import { ThumbnailRenderer } from "./thumbnails";
import { ApiError, type SlideInfo, type TemplateFile, type TemplateFolder } from "./types";

import "./taskpane.css";

/**
 * Task pane controller.
 *
 * Key change from v1: opening a template downloads the deck exactly once. Those
 * same bytes serve slide metadata (parsed in-browser) and the eventual insert,
 * while thumbnails come from the separately rendered PDF. v1 made three API
 * calls and downloaded the deck three times.
 */

type OpenTemplate = {
  file: TemplateFile;
  slides: SlideInfo[];
  pptxBytes: ArrayBuffer;
  renderer: ThumbnailRenderer;
  container: HTMLElement;
  button: HTMLElement;
  selected: Set<string>;
  abort: AbortController;
};

let open: OpenTemplate | null = null;

Office.onReady(async (info) => {
  if (info.host !== Office.HostType.PowerPoint) {
    setStatus("This add-in runs in PowerPoint.");
    return;
  }

  const refreshButton = document.getElementById("refresh");
  refreshButton?.addEventListener("click", () => void loadTree({ forceRefresh: true }));

  await loadTree();
});

function setStatus(message: string, kind: "info" | "error" = "info"): void {
  const status = document.getElementById("status");
  if (!status) return;
  status.textContent = message;
  status.classList.toggle("is-error", kind === "error");
}

function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.correlationId
      ? `${error.message} (ref ${error.correlationId.slice(0, 8)})`
      : error.message;
  }
  if (error instanceof Error) return error.message;
  // If it's an object-like payload (e.g., { error, correlationId }), stringify
  if (typeof error === "object" && error !== null) {
    try {
      // Prefer structured message fields if present
      // common server shape: { error: string, correlationId: string }
      const anyErr = error as any;
      if (anyErr.error) {
        return anyErr.correlationId ? `${anyErr.error} (ref ${String(anyErr.correlationId).slice(0,8)})` : anyErr.error;
      }
      return JSON.stringify(error);
    } catch {
      return Object.prototype.toString.call(error);
    }
  }

  return String(error);
}

async function loadTree(options: { forceRefresh?: boolean } = {}): Promise<void> {
  const treeEl = document.getElementById("tree");
  if (!treeEl) return;

  closeOpenTemplate();
  setStatus("Loading templates…");

  try {
    const tree = await getTemplateTree(options);
    treeEl.replaceChildren(renderFolder(tree, 0, true));
    setStatus("");
  } catch (error) {
    treeEl.replaceChildren();
    setStatus(`Could not load templates: ${describeError(error)}`, "error");
  }
}

function renderFolder(folder: TemplateFolder, level: number, expanded = false): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "tree-folder";

  const row = document.createElement("button");
  row.type = "button";
  row.className = "tree-row tree-folder-row";
  row.style.setProperty("--level", String(level));
  row.setAttribute("aria-expanded", String(expanded));

  const arrow = document.createElement("span");
  arrow.className = "tree-arrow";
  arrow.setAttribute("aria-hidden", "true");
  arrow.textContent = expanded ? "▾" : "▸";

  const label = document.createElement("span");
  label.className = "tree-label";
  label.textContent = folder.name || "Templates";

  row.append(arrow, label);

  const children = document.createElement("div");
  children.className = "tree-children";
  children.hidden = !expanded;

  for (const child of folder.children ?? []) {
    children.appendChild(renderFolder(child, level + 1));
  }
  for (const file of folder.files ?? []) {
    children.appendChild(renderFile(file, level + 1));
  }

  if (!folder.children?.length && !folder.files?.length) {
    const empty = document.createElement("div");
    empty.className = "tree-empty";
    empty.style.setProperty("--level", String(level + 1));
    empty.textContent = "No templates here";
    children.appendChild(empty);
  }

  row.addEventListener("click", () => {
    const collapse = !children.hidden;
    children.hidden = collapse;
    row.setAttribute("aria-expanded", String(!collapse));
    arrow.textContent = collapse ? "▸" : "▾";
  });

  wrapper.append(row, children);
  return wrapper;
}

function renderFile(file: TemplateFile, level: number): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "tree-file-wrapper";

  const button = document.createElement("button");
  button.type = "button";
  button.className = "tree-row tree-file-row";
  button.style.setProperty("--level", String(level));
  button.title = file.name;
  button.setAttribute("aria-expanded", "false");

  const spacer = document.createElement("span");
  spacer.className = "tree-arrow-placeholder";
  spacer.setAttribute("aria-hidden", "true");

  const label = document.createElement("span");
  label.className = "tree-label";
  label.textContent = file.name;

  button.append(spacer, label);

  const panel = document.createElement("div");
  panel.className = "inline-slides";
  panel.hidden = true;
  panel.style.setProperty("--level", String(level + 1));

  button.addEventListener("click", () => {
    // Second click on the already-open file collapses it.
    if (open?.button === button) {
      closeOpenTemplate();
      return;
    }
    void openTemplate(file, button, panel);
  });

  wrapper.append(button, panel);
  return wrapper;
}

function closeOpenTemplate(): void {
  if (!open) return;

  open.abort.abort();
  open.renderer.dispose();
  open.container.hidden = true;
  open.container.replaceChildren();
  open.button.classList.remove("is-selected");
  open.button.setAttribute("aria-expanded", "false");

  open = null;
}

async function openTemplate(
  file: TemplateFile,
  button: HTMLElement,
  container: HTMLElement
): Promise<void> {
  closeOpenTemplate();

  button.classList.add("is-selected");
  button.setAttribute("aria-expanded", "true");
  container.hidden = false;

  const loading = document.createElement("div");
  loading.className = "inline-slides-message";
  loading.textContent = "Loading slides…";
  container.replaceChildren(loading);

  const abort = new AbortController();
  const renderer = new ThumbnailRenderer();

  setStatus(`Loading ${file.name}…`);

  try {
    const links = await getTemplateLinks(file.id);

    // Deck bytes and rendered PDF fetched in parallel, both straight from
    // SharePoint. Neither passes through the Function App.
    const [pptxBytes, pdfBytes] = await Promise.all([
      fetchTemplateBytes(links, "pptx", abort.signal),
      fetchTemplateBytes(links, "pdf", abort.signal).catch(() => null),
    ]);

    if (abort.signal.aborted) return;

    const slides = await parseSlides(pptxBytes);

    open = {
      file,
      slides,
      pptxBytes,
      renderer,
      container,
      button,
      selected: new Set<string>(),
      abort,
    };

    renderSlidesPanel(open);

    if (pdfBytes) {
      try {
        const contentVersion = (links as any).contentVersion || (links as any).cTag || (links as any).eTag || null;
        await renderer.load(pdfBytes, { itemId: links.id, contentVersion, renderVersion: 1 });
        attachThumbnails(open);
      } catch {
        setStatus("Preview images unavailable — slide names still work.");
      }
    }

    setStatus("");
  } catch (error) {
    renderer.dispose();

    if (abort.signal.aborted) return;

    const box = document.createElement("div");
    box.className = "inline-slides-error";
    box.textContent = `Could not open this template: ${describeError(error)}`;
    container.replaceChildren(box);

    setStatus("");
  }
}

function renderSlidesPanel(state: OpenTemplate): void {
  const { file, slides, container } = state;

  const header = document.createElement("div");
  header.className = "inline-slides-header";

  const title = document.createElement("div");
  title.className = "inline-slides-title";
  title.textContent = file.name;

  const actions = document.createElement("div");
  actions.className = "slides-actions";

  const insertAll = document.createElement("button");
  insertAll.type = "button";
  insertAll.className = "button button-secondary";
  insertAll.textContent = "Insert all";
  insertAll.disabled = slides.length === 0;

  const insertSelected = document.createElement("button");
  insertSelected.type = "button";
  insertSelected.className = "button button-primary";
  insertSelected.textContent = "Insert selected";
  insertSelected.disabled = true;

  actions.append(insertAll, insertSelected);
  header.append(title, actions);

  const grid = document.createElement("div");
  grid.className = "inline-slides-grid";

  for (const slide of slides) {
    grid.appendChild(renderSlideCard(state, slide, insertSelected));
  }

  insertAll.addEventListener("click", () => {
    void insertSlides(state, slides.map((slide) => slide.sourceSlideId), insertAll, insertSelected);
  });

  insertSelected.addEventListener("click", () => {
    void insertSlides(state, [...state.selected], insertAll, insertSelected);
  });

  if (!slides.length) {
    const empty = document.createElement("div");
    empty.className = "inline-slides-message";
    empty.textContent = "No slides found in this template.";
    container.replaceChildren(header, empty);
    return;
  }

  const helper = document.createElement("div");
  helper.className = "inline-slides-helper";
  helper.textContent = "Pick slides to insert, or insert the whole deck.";

  container.replaceChildren(header, helper, grid);
}

function renderSlideCard(
  state: OpenTemplate,
  slide: SlideInfo,
  insertSelected: HTMLButtonElement
): HTMLElement {
  const card = document.createElement("button");
  card.type = "button";
  card.className = "slide-card";
  card.title = slide.title;
  card.dataset.pageNumber = String(slide.slideNumber);
  card.setAttribute("aria-pressed", "false");

  const check = document.createElement("span");
  check.className = "slide-check";
  check.setAttribute("aria-hidden", "true");
  check.textContent = "✓";

  const thumb = document.createElement("div");
  thumb.className = "slide-thumb is-loading";
  thumb.textContent = `Slide ${slide.slideNumber}`;

  const meta = document.createElement("div");
  meta.className = "slide-meta";
  meta.textContent = slide.title;

  card.append(check, thumb, meta);

  card.addEventListener("click", () => {
    const isSelected = state.selected.has(slide.sourceSlideId);

    if (isSelected) {
      state.selected.delete(slide.sourceSlideId);
    } else {
      state.selected.add(slide.sourceSlideId);
    }

    card.classList.toggle("is-selected", !isSelected);
    card.setAttribute("aria-pressed", String(!isSelected));
    insertSelected.disabled = state.selected.size === 0;
  });

  return card;
}

function attachThumbnails(state: OpenTemplate): void {
  const cards = state.container.querySelectorAll<HTMLElement>(".slide-card");

  cards.forEach((card) => {
    const pageNumber = Number(card.dataset.pageNumber);
    const slot = card.querySelector<HTMLElement>(".slide-thumb");
    if (!pageNumber || !slot) return;
    state.renderer.observe(card, pageNumber, slot);
  });
}

async function insertSlides(
  state: OpenTemplate,
  sourceSlideIds: string[],
  insertAll: HTMLButtonElement,
  insertSelected: HTMLButtonElement
): Promise<void> {
  if (!sourceSlideIds.length) {
    setStatus("Select at least one slide.");
    return;
  }

  insertAll.disabled = true;
  insertSelected.disabled = true;
  setStatus(`Inserting ${sourceSlideIds.length} slide(s)…`);

  try {
    // Bytes are already in memory from opening the template — no second download.
    const base64 = await toBase64(state.pptxBytes);

    await PowerPoint.run(async (context) => {
      const selection = context.presentation.getSelectedSlides();
      selection.load("items/id");
      await context.sync();

      const lastSelected = selection.items[selection.items.length - 1];

      context.presentation.insertSlidesFromBase64(base64, {
        sourceSlideIds,
        ...(lastSelected ? { targetSlideId: lastSelected.id } : {}),
        formatting: PowerPoint.InsertSlideFormatting.keepSourceFormatting,
      });

      await context.sync();
    });

    setStatus(`Inserted ${sourceSlideIds.length} slide(s).`);
  } catch (error) {
    setStatus(`Could not insert slides: ${describeError(error)}`, "error");
  } finally {
    insertAll.disabled = false;
    insertSelected.disabled = state.selected.size === 0;
  }
}
